import React, { useEffect, useState } from "react";
import { Star, X } from "lucide-react";
import { fmtAmount } from "@/lib/stayflowApi";

// Projects a lat/lng to pixel coords within the map div and keeps a card glued
// next to that pin as the user pans/zooms. Rendered as normal DOM (buttons work).
export const MapAnchoredPopover = ({ map, point, meta, onClose, onView }) => {
  const [px, setPx] = useState(null);

  useEffect(() => {
    if (!map || !point || point.lat == null) { setPx(null); return; }
    const compute = () => {
      const b = map.getBounds();
      const div = map.getDiv();
      if (!b || !div) return;
      const ne = b.getNorthEast(), sw = b.getSouthWest();
      const w = div.offsetWidth, h = div.offsetHeight;
      const merc = (l) => Math.log(Math.tan(Math.PI / 4 + (l * Math.PI) / 360));
      let lngSpan = ne.lng() - sw.lng(); if (lngSpan <= 0) lngSpan += 360;
      let dLng = point.lng - sw.lng(); if (dLng < 0) dLng += 360;
      const x = (dLng / lngSpan) * w;
      const y = ((merc(ne.lat()) - merc(point.lat)) / (merc(ne.lat()) - merc(sw.lat()))) * h;
      setPx({ x, y });
    };
    compute();
    const l1 = map.addListener("bounds_changed", compute);
    const l2 = map.addListener("idle", compute);
    return () => { l1.remove(); l2.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, point?.lat, point?.lng]);

  if (!px || !meta) return null;

  return (
    <div
      className="absolute z-40 w-60 animate-in fade-in zoom-in-95 duration-150"
      style={{ left: px.x, top: px.y, transform: "translate(-50%, calc(-100% - 20px))" }}
      data-testid={`map-popover-${meta.id}`}
    >
      <div className="rounded-2xl bg-white shadow-2xl border border-black/10 overflow-hidden">
        <div className="relative h-28 bg-paper-muted">
          {meta.image && <img src={meta.image} alt={meta.title} className="w-full h-full object-cover" />}
          {meta.demo && <span className="absolute top-2 left-2 rounded-full bg-amber-400/95 text-amber-950 text-[0.65rem] font-bold px-2 py-0.5 shadow">DEMO</span>}
          <button onClick={onClose} data-testid="map-popover-close"
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow active:scale-95 transition"><X className="w-4 h-4 text-ink" /></button>
        </div>
        <div className="p-3">
          <p className="font-heading font-bold text-sm text-ink truncate">{meta.title}</p>
          <div className="mt-1 flex items-center justify-between">
            {meta.rating > 0
              ? <span className="inline-flex items-center gap-1 text-xs text-ink"><Star className="w-3.5 h-3.5 fill-[#F59E0B] text-[#F59E0B]" /> {meta.rating.toFixed(2)}</span>
              : <span className="text-xs text-ink-faint">New listing</span>}
            <span className="text-sm font-bold text-ink">{fmtAmount(meta.price)}<span className="font-normal text-ink-faint"> /night incl. GST</span></span>
          </div>
          {meta.demo ? (
            <p className="mt-2.5 rounded-lg bg-paper-muted text-ink-soft text-xs font-semibold py-2 text-center">Demo stay · preview only</p>
          ) : (
            <button onClick={onView} data-testid={`map-popover-view-${meta.id}`}
              className="mt-2.5 w-full rounded-lg bg-brand hover:bg-brand-dark text-white text-sm font-bold py-2.5 active:scale-95 transition">View stay</button>
          )}
        </div>
      </div>
      {/* pointer */}
      <span className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-white rotate-45 border-r border-b border-black/10" />
    </div>
  );
};

export default MapAnchoredPopover;
